import { useState } from "react";
import { toast } from "sonner";
import { Info } from "lucide-react";
import { Badge, CategoryBadge, EmptyState, ErrorState } from "@/components/DataTable";
import { PageHead } from "@/components/Filters";
import { api, errMsg } from "@/lib/api";
import { useFilterOptions } from "./hooks";

const TONE = { "Very High": "emerald", High: "blue", Medium: "amber", Low: "rose" };

const MultiPick = ({ label, options, selected, onToggle, testId }) => (
  <div>
    <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">{label}</p>
    <div className="mt-2 flex max-h-32 flex-wrap gap-1.5 overflow-auto" data-testid={testId}>
      {options.length === 0 && <span className="text-xs text-slate-400">No options published yet</span>}
      {options.map((o) => (
        <button
          key={o}
          data-testid={`${testId}-${o}`}
          onClick={() => onToggle(o)}
          className={`border px-2 py-1 text-xs transition-colors ${
            selected.includes(o)
              ? "border-[#1A365D] bg-[#1A365D] text-white"
              : "border-slate-300 bg-white text-slate-700 hover:border-[#1A365D]"
          }`}
        >
          {o}
        </button>
      ))}
    </div>
  </div>
);

export default function Probability() {
  const opts = useFilterOptions();
  const [air, setAir] = useState("");
  const [states, setStates] = useState([]);
  const [branches, setBranches] = useState([]);
  const [res, setRes] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const toggle = (setter) => (v) => setter((p) => (p.includes(v) ? p.filter((x) => x !== v) : [...p, v]));

  const run = async () => {
    if (!air) return toast.error("Enter your AIR");
    setBusy(true);
    setError(null);
    try {
      const { data } = await api.post("/probability", { air: Number(air), states, branches });
      setRes(data);
    } catch (e) {
      setError(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="probability-page">
      <PageHead
        title="Admission Probability"
        subtitle="Conservative, historical-evidence-only labels. If the data is weak, NeetOne shows nothing rather than guessing."
      />

      <div className="flex items-start gap-2 border border-slate-200 bg-slate-50 px-4 py-3 text-xs text-slate-600">
        <Info className="mt-0.5 h-4 w-4 shrink-0 text-[#1A365D]" />
        In v1, quota and category are browsing filters only — they are not used as probability inputs.
      </div>

      <div className="grid gap-4 border border-slate-200 bg-white p-4 lg:grid-cols-[200px_1fr_1fr]">
        <label className="flex flex-col gap-1">
          <span className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">Your AIR</span>
          <input
            data-testid="air-input"
            type="number"
            value={air}
            onChange={(e) => setAir(e.target.value)}
            placeholder="12400"
            className="h-9 border border-slate-300 px-2 font-mono-num text-sm focus:outline-none focus:ring-2 focus:ring-[#1A365D]"
          />
        </label>
        <MultiPick label="Preferred states" options={opts.states} selected={states} onToggle={toggle(setStates)} testId="state-pick" />
        <MultiPick label="Preferred branches" options={opts.branches.slice(0, 40)} selected={branches} onToggle={toggle(setBranches)} testId="branch-pick" />
        <div className="lg:col-span-3">
          <button
            data-testid="run-probability-btn"
            disabled={busy}
            onClick={run}
            className="h-9 bg-[#1A365D] px-5 text-xs font-semibold uppercase tracking-widest text-white hover:bg-[#122845] disabled:opacity-40"
          >
            {busy ? "Analysing…" : "Estimate probability"}
          </button>
        </div>
      </div>

      {error && <ErrorState message={error} />}

      {res?.insufficient_data && (
        <EmptyState
          testId="insufficient-data-state"
          title="Insufficient historical data"
          hint={res.reason || "We do not have enough comparable records to produce a reliable label. No estimate will be shown."}
        />
      )}

      {res && !res.insufficient_data && (
        <div className="border border-slate-200 bg-white overflow-auto" data-testid="probability-results">
          <table className="data-table">
            <thead>
              <tr>
                <th style={{ minWidth: 340 }}>College</th>
                <th style={{ minWidth: 160 }}>Branch</th>
                <th style={{ width: 130 }}>Quota</th>
                <th className="text-right" style={{ width: 170 }}>Historical closing rank</th>
                <th className="text-right" style={{ width: 90 }}>Seats</th>
                <th style={{ width: 130 }}>Probability</th>
              </tr>
            </thead>
            <tbody>
              {res.results.map((r, i) => (
                <tr key={`${r.college}|${r.branch}|${r.quota || ""}|${r.category || ""}`} data-testid={`prob-row-${i}`}>
                  <td className="whitespace-normal" title={r.college}>{r.college}</td>
                  <td>{r.branch}</td>
                  <td>{r.quota ? <Badge tone="blue" width="quota">{r.quota}</Badge> : <span className="text-slate-400">NA</span>}</td>
                  <td className="col-highlight text-right">
                    {r.best_closing_rank ? Number(r.best_closing_rank).toLocaleString("en-IN") : "—"}
                  </td>
                  <td className="text-right font-mono-num">{r.seats}</td>
                  <td>
                    {r.label ? <Badge tone={TONE[r.label]}>{r.label}</Badge> : <span className="text-xs text-slate-400">{r.note}</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {res.locked && (
            <p className="border-t border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900" data-testid="prob-locked-note">
              Trial view — showing {res.results.length} of {res.total} matches. Upgrade to MasterPlan for the full list.
            </p>
          )}
        </div>
      )}
    </div>
  );
}
